/* KPC eBay-style variant dropdown
   Replaces native dropdown UI but keeps the underlying <select>
   wired so Shopify's variant-selects component still receives change
   events. Behaves as listbox on desktop and bottom-sheet on mobile.

   Mobile portal: panel and backdrop are moved to document.body when
   opened so position:fixed escapes any transform-containing-block
   ancestor (Dawn's scroll-trigger animations apply transform to the
   PDP info wrapper, which otherwise traps fixed elements). */

/* Desktop: keep thumb-slider height in sync with main image height so
   the left strip's bottom edge aligns with the bottom of the main image
   (eBay pattern). Pure CSS can't express this because grid stretching
   would balloon to the thumb-list's natural content height (49 thumbs). */
function kpcSyncThumbHeight() {
  if (!window.matchMedia('(min-width: 750px)').matches) {
    document.querySelectorAll('.product--thumbnail .thumbnail-slider').forEach((el) => {
      el.style.height = '';
    });
    return;
  }
  document.querySelectorAll('.product--thumbnail').forEach((product) => {
    const main = product.querySelector('media-gallery > slider-component:not(.thumbnail-slider)');
    const thumb = product.querySelector('.thumbnail-slider');
    if (!main || !thumb) return;
    const h = main.getBoundingClientRect().height;
    if (h > 0) thumb.style.height = h + 'px';
  });
  // Update button disabled state after height change settles
  if (typeof kpcUpdateThumbButtons === 'function') {
    requestAnimationFrame(() => requestAnimationFrame(kpcUpdateThumbButtons));
  }
}
window.addEventListener('load', kpcSyncThumbHeight);
window.addEventListener('resize', kpcSyncThumbHeight);
// Observe main image size changes (lazy loading, variant changes that
// replace section innerHTML).
new MutationObserver(() => {
  // Re-run on DOM changes and observe newly-attached main sliders
  kpcSyncThumbHeight();
  document.querySelectorAll('.product--thumbnail media-gallery > slider-component:not(.thumbnail-slider)').forEach((main) => {
    if (main.__kpcResizeObserved) return;
    main.__kpcResizeObserved = true;
    new ResizeObserver(kpcSyncThumbHeight).observe(main);
  });
}).observe(document.body, { childList: true, subtree: true });

/* Desktop thumb-slider prev/next buttons (revealed on hover): the
   underlying <slider-component> assumes a horizontal slider. We
   override the click in capture phase to scroll the vertical
   thumb-list by ~80% of its visible height. */
document.addEventListener('click', (e) => {
  if (!e.target || typeof e.target.closest !== 'function') return;
  const btn = e.target.closest('.product--thumbnail .thumbnail-slider .slider-button');
  if (!btn) return;
  if (!window.matchMedia('(min-width: 750px)').matches) return;
  const slider = btn.closest('.thumbnail-slider');
  const list = slider && slider.querySelector('.thumbnail-list');
  if (!list) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  const step = list.clientHeight * 0.8;
  const delta = btn.classList.contains('slider-button--prev') ? -step : step;
  list.scrollBy({ top: delta, behavior: 'smooth' });
}, true);

/* Disable prev/next buttons when at scroll boundaries. */
function kpcUpdateThumbButtons() {
  if (!window.matchMedia('(min-width: 750px)').matches) return;
  document.querySelectorAll('.product--thumbnail .thumbnail-slider').forEach((slider) => {
    const list = slider.querySelector('.thumbnail-list');
    const prev = slider.querySelector('.slider-button--prev');
    const next = slider.querySelector('.slider-button--next');
    if (!list) return;
    const atTop = list.scrollTop <= 0;
    const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;
    if (prev) prev.toggleAttribute('disabled', atTop);
    if (next) next.toggleAttribute('disabled', atBottom);
    if (!list.__kpcScrollObserved) {
      list.__kpcScrollObserved = true;
      list.addEventListener('scroll', kpcUpdateThumbButtons, { passive: true });
    }
  });
}
window.addEventListener('load', kpcUpdateThumbButtons);
window.addEventListener('resize', kpcUpdateThumbButtons);

/* Desktop hover-to-preview: hovering a thumb temporarily previews the
   main image. Leaving the thumb-list area restores the committed
   (clicked) thumbnail. Click commits as the new committed state.

   State machine:
   - On entering thumb-list: snapshot current active media as
     `kpcCommittedMediaId` (only if no prior commit pending).
   - On hovering a different thumb: setActiveMedia(thumbId) — preview.
   - On leaving thumb-list: setActiveMedia(kpcCommittedMediaId) — restore.
   - On click inside thumb-list: update kpcCommittedMediaId to the
     clicked thumb so subsequent leave preserves the click. */
let kpcLastHoveredThumb = null;
let kpcInsideThumbList = false;
let kpcCommittedMediaId = null;

function kpcIsDesktopThumbActive() {
  return window.matchMedia('(min-width: 750px)').matches;
}

document.addEventListener('mouseover', (e) => {
  if (!kpcIsDesktopThumbActive()) return;
  if (!e.target || typeof e.target.closest !== 'function') return;
  const list = e.target.closest('.product--thumbnail .thumbnail-list');
  if (list) {
    if (!kpcInsideThumbList) {
      kpcInsideThumbList = true;
      const gallery = list.closest('media-gallery');
      const active = gallery && gallery.querySelector('.product__media-item.is-active');
      if (active) kpcCommittedMediaId = active.dataset.mediaId;
    }
    const thumb = e.target.closest('.thumbnail-list__item');
    if (thumb && thumb !== kpcLastHoveredThumb) {
      kpcLastHoveredThumb = thumb;
      const gallery = thumb.closest('media-gallery');
      const mediaId = thumb.dataset.target;
      if (gallery && mediaId && typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(mediaId, false);
      }
    }
  } else if (kpcInsideThumbList) {
    // Left the thumb-list area — restore committed image.
    kpcInsideThumbList = false;
    kpcLastHoveredThumb = null;
    if (kpcCommittedMediaId) {
      const gallery = document.querySelector('.product--thumbnail media-gallery');
      if (gallery && typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(kpcCommittedMediaId, false);
      }
    }
  }
});

/* Click commits: capture the clicked thumb id as the new committed
   target so leaving the list no longer reverts to the previous one. */
document.addEventListener('click', (e) => {
  if (!kpcIsDesktopThumbActive()) return;
  if (!e.target || typeof e.target.closest !== 'function') return;
  const thumb = e.target.closest('.product--thumbnail .thumbnail-list__item');
  if (!thumb) return;
  kpcCommittedMediaId = thumb.dataset.target;
}, true);

/* Thumb-strip → variant change. Tapping/clicking a thumbnail should
   switch the selected variant (not just the visible image). Each thumb
   is rendered with data-variant-option1/2/3 attributes pointing to the
   variant whose featured_media matches. We write those values to the
   underlying <select> in the picker and dispatch a single change event
   so Shopify's variant-selects fetches and renders the new variant.
   Applies on all viewports; thumbs without a matched variant are
   skipped via the data-variant-id guard. */
document.addEventListener('click', (e) => {
  if (!e.target || typeof e.target.closest !== 'function') return;
  const thumb = e.target.closest('.thumbnail-list__item, .kpc-variant-grid__item');
  if (!thumb) return;
  if (thumb.classList.contains('is-sold-out')) return;
  const variantId = thumb.dataset.variantId;
  if (!variantId) return;
  const productInfo = thumb.closest('product-info');
  if (!productInfo) return;
  const selects = productInfo.querySelectorAll('variant-selects select');
  if (selects.length === 0) return;
  let lastChanged = null;
  selects.forEach((select, i) => {
    const optKey = 'variantOption' + (i + 1);
    const val = thumb.dataset[optKey];
    if (val == null) return;
    if (select.value !== val) {
      select.value = val;
      lastChanged = select;
    }
  });
  if (lastChanged) {
    lastChanged.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* GA4 custom event. Fires on every variant pick from the thumbnail
     grid OR the gallery thumb strip. Used to measure whether the new
     grid picker (2026-05) reduces add_to_cart friction. */
  if (typeof window.gtag === 'function') {
    const isGrid = thumb.classList.contains('kpc-variant-grid__item');
    window.gtag('event', 'variant_grid_click', {
      variant_id: variantId,
      option1: thumb.dataset.variantOption1 || '',
      option2: thumb.dataset.variantOption2 || '',
      option3: thumb.dataset.variantOption3 || '',
      source: isGrid ? 'info_grid' : 'gallery_strip',
    });
  }
});

if (!customElements.get('kpc-variant-dropdown')) {
  customElements.define(
    'kpc-variant-dropdown',
    class KpcVariantDropdown extends HTMLElement {
      constructor() {
        super();
        this.onDocClick = this.onDocClick.bind(this);
        this.onKey = this.onKey.bind(this);
        this._portaled = false;
      }

      connectedCallback() {
        this.trigger = this.querySelector('.kpc-picker__trigger');
        this.panel = this.querySelector('.kpc-picker__panel');
        this.backdrop = this.querySelector('.kpc-picker__backdrop');
        this.closeBtn = this.querySelector('.kpc-picker__close');
        this.items = Array.from(this.querySelectorAll('.kpc-picker__item'));
        this.select = this.querySelector('select.kpc-picker__native');
        this.valueLabel = this.querySelector('[data-selected-value]');
        this.swatchEl = this.querySelector('[data-selected-swatch]');

        if (!this.trigger || !this.select) return;

        this.trigger.addEventListener('click', () => this.toggle());
        this.items.forEach((item) =>
          item.addEventListener('click', (e) => {
            if (item.classList.contains('is-unavailable')) {
              e.stopPropagation();
              if (item.classList.contains('is-shaking')) return;
              item.classList.add('is-shaking');
              if (navigator.vibrate) navigator.vibrate(12);
              setTimeout(() => item.classList.remove('is-shaking'), 420);
              return;
            }
            this.choose(item);
            e.stopPropagation();
          })
        );
        if (this.backdrop) this.backdrop.addEventListener('click', () => this.close());
        if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());

        this.select.addEventListener('change', () => this.syncFromSelect());
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocClick);
        document.removeEventListener('keydown', this.onKey);
        document.body.classList.remove('kpc-picker-sheet-open');
        this._unportal(true);
      }

      isMobile() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      _portal() {
        if (this._portaled || !this.panel) return;
        this._panelOrigParent = this.panel.parentElement;
        this._panelOrigNext = this.panel.nextSibling;
        document.body.appendChild(this.panel);
        if (this.backdrop) {
          this._backdropOrigParent = this.backdrop.parentElement;
          this._backdropOrigNext = this.backdrop.nextSibling;
          document.body.appendChild(this.backdrop);
        }
        this._portaled = true;
      }

      _unportal(immediate) {
        if (!this._portaled) return;
        if (this._unportalTimer) {
          clearTimeout(this._unportalTimer);
          this._unportalTimer = null;
        }
        const doRestore = () => {
          if (!this._portaled) return;
          if (this._panelOrigParent && this.panel) {
            try {
              this._panelOrigParent.insertBefore(this.panel, this._panelOrigNext);
            } catch (e) {
              if (this.panel.parentElement === document.body) document.body.removeChild(this.panel);
            }
          }
          if (this.backdrop && this._backdropOrigParent) {
            try {
              this._backdropOrigParent.insertBefore(this.backdrop, this._backdropOrigNext);
            } catch (e) {
              if (this.backdrop.parentElement === document.body) document.body.removeChild(this.backdrop);
            }
          }
          this._portaled = false;
          this._unportalTimer = null;
        };
        if (immediate) {
          doRestore();
        } else {
          this._unportalTimer = setTimeout(doRestore, 280);
        }
      }

      toggle() {
        this.classList.contains('is-open') ? this.close() : this.open();
      }

      open() {
        if (this.isMobile()) this._portal();
        this.classList.add('is-open');
        if (this.panel) this.panel.classList.add('is-open');
        if (this.backdrop) this.backdrop.classList.add('is-open');
        this.trigger.setAttribute('aria-expanded', 'true');
        document.addEventListener('click', this.onDocClick);
        document.addEventListener('keydown', this.onKey);
        if (this.isMobile()) document.body.classList.add('kpc-picker-sheet-open');
        const sel = (this.panel || this).querySelector('.kpc-picker__item.is-selected');
        if (sel) sel.scrollIntoView({ block: 'nearest' });
      }

      close() {
        this.classList.remove('is-open');
        if (this.panel) this.panel.classList.remove('is-open');
        if (this.backdrop) this.backdrop.classList.remove('is-open');
        this.trigger.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.onDocClick);
        document.removeEventListener('keydown', this.onKey);
        document.body.classList.remove('kpc-picker-sheet-open');
        this._unportal(false);
      }

      onDocClick(e) {
        if (this.contains(e.target)) return;
        if (this.panel && this.panel.contains(e.target)) return;
        if (this.backdrop && this.backdrop.contains(e.target)) return;
        this.close();
      }

      onKey(e) {
        if (e.key === 'Escape') this.close();
      }

      choose(item) {
        const value = item.dataset.value;
        if (!this.select) return;
        this.select.value = value;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncFromSelect();
        this.close();
      }

      syncFromSelect() {
        const value = this.select.value;
        this.items.forEach((item) => {
          const selected = item.dataset.value === value;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-selected', selected ? 'true' : 'false');
        });
        if (this.valueLabel) this.valueLabel.textContent = value;
      }
    }
  );
}
